// client/src/pages/OnboardingPage.jsx
//
// First-time setup form for new distributors signing up. Collects the minimum
// to make their account useful: company, name, email, phone, and an optional
// (but encouraged) business address for sales tax calculation and reporting.
//
// Design notes (June 2026 refresh):
//   - Single page, no multi-step wizard. Faster perceived completion.
//   - Address is optional and visually demoted into its own section so it
//     doesn't feel mandatory. US + Canada optimized — country dropdown lists
//     them first, then the rest alphabetical for anomalies.
//   - State field becomes a dropdown for US/CA, free text for everyone else.
//   - On submit, the server auto-generates the storefront subdomain from the
//     company name. The user sees it in the welcome modal that follows.
//
// After save, App.jsx routes the user to /dashboard and shows the welcome
// modal.

import { useState, useMemo } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';

const G = BRAND.colors.primary;

// US states + Canadian provinces. Order by full name within each country.
const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['DC','District of Columbia'],
  ['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],
  ['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],
  ['ME','Maine'],['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],
  ['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],
  ['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],
  ['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],['OR','Oregon'],
  ['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],
  ['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],['VA','Virginia'],
  ['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
];

const CA_PROVINCES = [
  ['AB','Alberta'],['BC','British Columbia'],['MB','Manitoba'],['NB','New Brunswick'],
  ['NL','Newfoundland and Labrador'],['NS','Nova Scotia'],['NT','Northwest Territories'],
  ['NU','Nunavut'],['ON','Ontario'],['PE','Prince Edward Island'],['QC','Quebec'],
  ['SK','Saskatchewan'],['YT','Yukon'],
];

// Country dropdown — US and CA first (the design target), then a short list
// of other common countries. We intentionally don't ship 195 countries here;
// users from elsewhere can pick "Other" and we collect free-text state/postal.
const COUNTRIES = [
  ['US','United States'],
  ['CA','Canada'],
  ['MX','Mexico'],
  ['GB','United Kingdom'],
  ['AU','Australia'],
  ['DE','Germany'],
  ['FR','France'],
  ['OTHER','Other'],
];

// Per-country labels so US users see "ZIP code" and UK users see "Postcode".
const POSTAL_LABEL = { US: 'ZIP code', CA: 'Postal code', GB: 'Postcode', AU: 'Postcode' };

export default function OnboardingPage({ dist, getToken, onComplete, navigate, onSave }) {
  const [company, setCompany] = useState(dist?.company || '');
  const [name,    setName]    = useState(dist?.name || '');
  const [email,   setEmail]   = useState(dist?.email || dist?.contact_email || '');
  const [phone,   setPhone]   = useState(dist?.contact_phone || '');

  // Address fields — all optional individually but the section is one logical block.
  const [country, setCountry] = useState(dist?.address_country || 'US');
  const [line1,   setLine1]   = useState(dist?.address_line1 || '');
  const [line2,   setLine2]   = useState(dist?.address_line2 || '');
  const [city,    setCity]    = useState(dist?.address_city || '');
  const [state,   setState]   = useState(dist?.address_state || '');
  const [postal,  setPostal]  = useState(dist?.address_postal_code || '');

  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [acceptedToS, setAcceptedToS] = useState(false);

  // The state field switches between dropdown (US/CA) and free text (everyone else).
  const stateOptions = useMemo(() => {
    if (country === 'US') return US_STATES;
    if (country === 'CA') return CA_PROVINCES;
    return null; // null = render as free-text input
  }, [country]);

  const postalLabel = POSTAL_LABEL[country] || 'Postal code';

  // Reset state when switching country between dropdown countries — otherwise
  // a US "WA" stays selected when you flip to Canada and the dropdown shows
  // it as cleared but the state variable still has stale data.
  const onCountryChange = (next) => {
    setCountry(next);
    if (state && ((country === 'US' && next !== 'US') || (country === 'CA' && next !== 'CA'))) {
      setState('');
    }
  };

  const canSubmit = company.trim() && name.trim() && email.trim() && phone.trim() && acceptedToS;

  const save = async () => {
    if (!canSubmit) {
      setError('Please fill in all required fields and accept the Terms of Service.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const t = await getToken();
      const payload = {
        company:             company.trim(),
        name:                name.trim(),
        contact_email:       email.trim(),
        contact_phone:       phone.trim(),
        // Address fields — only send what's actually filled so we don't
        // overwrite anything to empty string when the user skipped them.
        // Server treats undefined as "don't update this column."
        ...(line1.trim()  && { address_line1:       line1.trim() }),
        ...(line2.trim()  && { address_line2:       line2.trim() }),
        ...(city.trim()   && { address_city:        city.trim() }),
        ...(state.trim()  && { address_state:       state.trim() }),
        ...(postal.trim() && { address_postal_code: postal.trim() }),
        // Always send country since it has a default and the server may use
        // it to auto-set tax behavior later.
        address_country: country,
      };
      const updated = await api.withToken(t).updateMe(payload);
      // Refresh the in-memory distributor record with the just-saved profile
      // — Topbar reads `dist.company` for the header, and it was previously
      // never updated here. The database had the real company name right
      // after save; the client kept showing "Untitled Company" (the
      // checkout webhook's placeholder) for the rest of the session because
      // nothing ever told React the record had changed.
      onSave?.(updated);
      // Accept Terms of Service, then re-read billing status so the parent's
      // gate has the REAL shape ({ acceptedToS, hasAccess, ... }). Passing the
      // distributor object here instead would leave hasAccess undefined and
      // bounce the user straight to the lockout screen.
      await api.withToken(t).acceptToS();
      let status = null;
      try { status = await api.withToken(t).getBillingStatus(); } catch { /* non-fatal */ }
      onComplete(status || { ...updated, acceptedToS: true, hasAccess: true });
      // The gate above stops rendering this page once billingStatus reflects
      // acceptance, but nothing else ever moved the screen off the
      // "onboarding" hash it arrived on — that hash has no matching page in
      // the normal sidebar layout, so the user landed on a blank content
      // area even though onboarding had genuinely finished. Land on the
      // dashboard, the same place returning to the app afterward would.
      navigate?.('dashboard');
    } catch (e) {
      setError('Could not save your account: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Styles (defined inline so we don't drag a CSS module along) ────────
  const containerStyle = {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    minHeight: '100vh', background: '#F8FAFC', padding: '48px 24px',
  };
  const cardStyle = {
    background: '#fff', borderRadius: 16, border: '1px solid #E2E8F0',
    padding: 48, maxWidth: 520, width: '100%',
  };
  const labelStyle = {
    fontSize: 12, fontWeight: 500, color: '#374151',
    marginBottom: 5, display: 'block',
  };
  const inputStyle = {
    width: '100%', border: '1px solid #E2E8F0', borderRadius: 8,
    padding: '10px 12px', fontSize: 13, fontFamily: 'inherit',
    outline: 'none', color: '#0F172A', background: '#fff', boxSizing: 'border-box',
  };
  const sectionHeading = {
    fontSize: 12, fontWeight: 600, color: '#0F172A',
    letterSpacing: '0.4px', textTransform: 'uppercase',
    marginBottom: 4, marginTop: 28,
  };
  const sectionSubhead = {
    fontSize: 12, color: '#94A3B8', marginBottom: 16, lineHeight: 1.5,
  };
  const row = { display: 'flex', gap: 10 };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{
          fontSize: 13, color: G, fontWeight: 600,
          letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: 14,
        }}>
          Welcome
        </div>
        <div style={{
          fontSize: 28, fontWeight: 700, color: '#0F172A',
          letterSpacing: '-0.5px', lineHeight: 1.15, marginBottom: 10,
        }}>
          Let's get your account set up.
        </div>
        <div style={{ fontSize: 14, color: '#475569', marginBottom: 32, lineHeight: 1.5 }}>
          Takes about a minute. You can update any of this later in your account settings.
        </div>

        {/* ── About you ──────────────────────────────────────────── */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Company name <span style={{ color: '#EF4444' }}>*</span></label>
          <input value={company} onChange={e => setCompany(e.target.value)}
            placeholder="Seattle Custom Printing" style={inputStyle} />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Your name <span style={{ color: '#EF4444' }}>*</span></label>
          <input value={name} onChange={e => setName(e.target.value)}
            placeholder="Tyler Cook" style={inputStyle} />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Business email <span style={{ color: '#EF4444' }}>*</span></label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="you@yourcompany.com" style={inputStyle} />
        </div>

        <div style={{ marginBottom: 8 }}>
          <label style={labelStyle}>Phone <span style={{ color: '#EF4444' }}>*</span></label>
          <input value={phone} onChange={e => setPhone(e.target.value)}
            placeholder="206-555-0100" style={inputStyle} />
        </div>

        {/* ── Business address ───────────────────────────────────── */}
        <div style={sectionHeading}>Business address</div>
        <div style={sectionSubhead}>
          Optional, but recommended — we use this for sales tax calculation and to keep your records accurate.
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Country</label>
          <select value={country} onChange={e => onCountryChange(e.target.value)} style={inputStyle}>
            {COUNTRIES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Street address</label>
          <input value={line1} onChange={e => setLine1(e.target.value)}
            placeholder="1234 Main Street" style={inputStyle} />
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Apartment, suite, etc. <span style={{ color: '#94A3B8', fontWeight: 400 }}>(optional)</span></label>
          <input value={line2} onChange={e => setLine2(e.target.value)}
            placeholder="Suite 200" style={inputStyle} />
        </div>

        <div style={{ ...row, marginBottom: 10 }}>
          <div style={{ flex: 2 }}>
            <label style={labelStyle}>City</label>
            <input value={city} onChange={e => setCity(e.target.value)}
              placeholder="Seattle" style={inputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>{country === 'CA' ? 'Province' : country === 'US' ? 'State' : 'State / Province'}</label>
            {stateOptions ? (
              <select value={state} onChange={e => setState(e.target.value)} style={inputStyle}>
                <option value="">—</option>
                {stateOptions.map(([code, label]) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
            ) : (
              <input value={state} onChange={e => setState(e.target.value)}
                placeholder="Region" style={inputStyle} />
            )}
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>{postalLabel}</label>
            <input value={postal} onChange={e => setPostal(e.target.value)}
              placeholder={country === 'US' ? '98101' : country === 'CA' ? 'V6B 1A1' : ''} style={inputStyle} />
          </div>
        </div>

        {/* ── Terms of Service ───────────────────────────────────── */}
        <div style={sectionHeading}>Terms of Service</div>
        <div style={{
          background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8,
          padding: 12, marginBottom: 16, fontSize: 12, color: '#475569',
          lineHeight: 1.6, maxHeight: 200, overflowY: 'auto',
        }}>
          <p><strong>1. Acceptance of Terms</strong></p>
          <p>By using PromoMerge, you agree to these Terms of Service and our Privacy Policy. If you do not agree, please do not use the service.</p>
          
          <p><strong>2. Use License</strong></p>
          <p>PromoMerge grants you a limited, non-exclusive license to use this service for your promotional products business. You may not resell, redistribute, or use this service on behalf of others without authorization.</p>
          
          <p><strong>3. Payment and Billing</strong></p>
          <p>You agree to pay all fees associated with your selected plan. Subscription plans renew automatically on the date specified. You can cancel anytime in your account settings.</p>
          
          <p><strong>4. Stripe Payment Processing</strong></p>
          <p>When using invoice payment collection, your customers' payments are processed through Stripe. By using this feature, you agree to Stripe's terms and conditions.</p>
          
          <p><strong>5. User Responsibilities</strong></p>
          <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree not to use the service for unlawful purposes.</p>
          
          <p><strong>6. Limitation of Liability</strong></p>
          <p>PromoMerge is provided "as is." We are not liable for indirect, incidental, or consequential damages arising from your use of the service.</p>
          
          <p><strong>7. Changes to Terms</strong></p>
          <p>We reserve the right to modify these terms at any time. Continued use of the service constitutes acceptance of updated terms.</p>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 20 }}>
          <input 
            type="checkbox" 
            checked={acceptedToS} 
            onChange={e => setAcceptedToS(e.target.checked)}
            style={{ marginTop: 4, cursor: 'pointer', width: 18, height: 18 }}
          />
          <label style={{ fontSize: 13, color: '#374151', cursor: 'pointer', lineHeight: 1.5 }}>
            I have read and accept the Terms of Service
          </label>
        </div>

        {/* ── Submit ─────────────────────────────────────────────── */}
        {error && (
          <div style={{
            marginTop: 16, padding: '10px 12px',
            background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8,
            fontSize: 12, color: '#991B1B',
          }}>
            {error}
          </div>
        )}

        <button onClick={save} disabled={saving || !canSubmit}
          style={{
            width: '100%', marginTop: 24,
            background: !canSubmit ? '#E2E8F0' : G,
            border: 'none', borderRadius: 10, padding: 14,
            color: '#fff', fontSize: 14, fontWeight: 500,
            cursor: !canSubmit ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            transition: 'background-color 0.15s',
          }}>
          {saving ? 'Setting up your account…' : 'Get started →'}
        </button>

        <div style={{ marginTop: 18, fontSize: 11, color: '#94A3B8', textAlign: 'center', lineHeight: 1.5 }}>
          Required fields are marked with <span style={{ color: '#EF4444' }}>*</span>. Everything else is optional.
        </div>
      </div>
    </div>
  );
}
